import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Typography, Table, Avatar, Input, Tag, Space, Statistic, Row, Col, message } from 'antd';
import { UserOutlined, PhoneOutlined, SearchOutlined, TeamOutlined, DollarOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import api from '../../../api/axios';

const { Text, Title } = Typography;
const IMG = process.env.REACT_APP_URL;

const formatPrice = (p) => {
    if (!p && p !== 0) return '0';
    return Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

function Clients() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const fetchOrders = useCallback(() => {
        setLoading(true);
        api.get('/orders/my')
            .then(res => setOrders(res.data?.data || []))
            .catch(() => message.error('Mijozlar ro\'yxatini yuklashda xatolik'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    const clients = useMemo(() => {
        const map = {};
        orders.forEach(o => {
            if (!o.users_id) return;
            if (!map[o.users_id]) {
                map[o.users_id] = {
                    id: o.users_id,
                    name: o.client_name || '—',
                    phone: o.client_phone || '',
                    avatar: o.client_avatar,
                    orders_count: 0,
                    total_spent: 0,
                    last_order: null,
                    statuses: {},
                };
            }
            const c = map[o.users_id];
            c.orders_count++;
            c.total_spent += Number(o.summa || 0);
            if (!c.last_order || o.id > c.last_order.id) c.last_order = o;
            c.statuses[o.status] = (c.statuses[o.status] || 0) + 1;
        });
        return Object.values(map).sort((a, b) => b.orders_count - a.orders_count);
    }, [orders]);

    const filtered = useMemo(() => {
        if (!search.trim()) return clients;
        const q = search.toLowerCase();
        return clients.filter(c => c.name?.toLowerCase().includes(q) || c.phone?.includes(q));
    }, [clients, search]);

    const totalRevenue = useMemo(() => clients.reduce((s, c) => s + c.total_spent, 0), [clients]);

    const columns = [
        {
            title: 'Mijoz', dataIndex: 'name', key: 'name',
            render: (name, rec) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar size={36} icon={<UserOutlined />} src={rec.avatar ? IMG + rec.avatar : undefined} />
                    <div>
                        <Text strong style={{ fontSize: 13 }}>{name}</Text>
                        {rec.phone && <div><Text type="secondary" style={{ fontSize: 11 }}><PhoneOutlined /> {rec.phone}</Text></div>}
                    </div>
                </div>
            ),
        },
        {
            title: 'Buyurtmalar', dataIndex: 'orders_count', key: 'orders_count', width: 120, sorter: (a, b) => a.orders_count - b.orders_count,
            render: (v) => <Tag color="blue">{v} ta</Tag>,
        },
        {
            title: 'Jami summa', dataIndex: 'total_spent', key: 'total_spent', width: 160, sorter: (a, b) => a.total_spent - b.total_spent,
            render: (v) => <Text strong style={{ color: '#22c55e' }}>{formatPrice(v)} so'm</Text>,
        },
        {
            title: 'Oxirgi buyurtma', dataIndex: 'last_order', key: 'last_order', width: 160,
            render: (o) => o ? <Text type="secondary" style={{ fontSize: 12 }}>{o.day}/{o.month}/{o.year}</Text> : '—',
        },
        {
            title: 'Holatlar', dataIndex: 'statuses', key: 'statuses', width: 200,
            render: (st) => (
                <Space size={4} wrap>
                    {Object.entries(st).map(([k, v]) => (
                        <Tag key={k} color={k === 'completed' ? 'green' : k === 'pending' ? 'orange' : k === 'confirmed' ? 'blue' : 'default'} style={{ fontSize: 10 }}>{k}: {v}</Tag>
                    ))}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Title level={4} style={{ marginBottom: 16 }}>Mijozlar</Title>

            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={8}>
                    <Card style={{ borderRadius: 12, background: 'rgba(102,126,234,0.14)' }} styles={{ body: { padding: '14px 18px' } }}>
                        <Statistic title="Jami mijozlar" value={clients.length} prefix={<TeamOutlined style={{ color: '#667eea' }} />} valueStyle={{ color: '#667eea' }} />
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card style={{ borderRadius: 12, background: 'rgba(34,197,94,0.14)' }} styles={{ body: { padding: '14px 18px' } }}>
                        <Statistic title="Jami buyurtmalar" value={orders.length} prefix={<ShoppingCartOutlined style={{ color: '#22c55e' }} />} valueStyle={{ color: '#22c55e' }} />
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card style={{ borderRadius: 12, background: 'rgba(245,158,11,0.14)' }} styles={{ body: { padding: '14px 18px' } }}>
                        <Statistic title="Jami tushum" value={formatPrice(totalRevenue)} suffix="so'm" prefix={<DollarOutlined style={{ color: '#f59e0b' }} />} valueStyle={{ color: '#f59e0b', fontSize: 18 }} />
                    </Card>
                </Col>
            </Row>

            <Card style={{ borderRadius: 14 }} styles={{ body: { padding: '16px' } }}>
                <Input
                    placeholder="Mijoz ismi yoki telefoni bo'yicha qidirish..."
                    prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ marginBottom: 12, borderRadius: 10 }}
                    size="large"
                    allowClear
                />
                <Table
                    dataSource={filtered}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    size="middle"
                    pagination={{ pageSize: 15, showSizeChanger: false }}
                    locale={{ emptyText: 'Mijozlar topilmadi' }}
                />
            </Card>
        </div>
    );
}

export default Clients;